import { QualityEditWarning } from './QualityEditWarning'
import { taskPrograms } from '@/lib/programs'
import { PublicationStatus } from './PublicationStatus'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { TaskHeading, TaskTabs } from '@/components/TaskHeading'
import { StageStepper } from '@/components/StageBadge'
import { needsBuilderReply, taskDisplayStage } from '@/lib/stages'
import { TaskContent } from '@/components/TaskContent'
import { RubricRefinement } from '@/components/RubricRefinement'
import { SubmissionPanel } from '@/components/SubmissionPanel'
import { ExamplesPanel } from '@/components/ExamplesPanel'
import { CoachPanel } from '@/components/CoachPanel'
import { FileList } from '@/components/FileList'
import { NextStep } from '@/components/NextStep'
import { MonitorPanel } from '@/pages/eng/MonitorPanel'
import { api } from '@/api/client'
import type { Role, TaskDetail } from '@/lib/types'
import { DraftReview } from './DraftReview'
import { ReviewPanel } from './ReviewPanel'
import { shownImplementedRubric } from './RubricComparison'
import { RubricLinkProvider, revealRubricCheck } from './rubricLinks'
import { TaskActivity } from './TaskActivity'
import { AuthoringReview } from '@/components/AuthoringReview'
import { ReturnedQualityFeedback } from './ReturnedQualityFeedback'
import { Icon } from '@/components/Icon'
import { Byline, feedbackSource } from '@/components/PersonName'
import { fmtDate } from '@/lib/format'
import { RetirementNotice, TaskLifecycle } from './TaskLifecycle'
import WorkflowReview from '@/pages/scientific/WorkflowReview'
import WorkflowAuthoring from '@/pages/scientific/WorkflowAuthoring'

export function TaskFlow({
  data,
  role,
  onSaved,
}: {
  data: TaskDetail
  role: Role
  onSaved: (data: TaskDetail) => void
}) {
  const { task, files } = data
  const [params, setParams] = useSearchParams()
  // A rollout result opens its check in the Rubric section.
  const openRubricCheck = useCallback((checkId: string) => {
    setParams((current) => { const next = new URLSearchParams(current); next.set('section', 'rubric'); return next })
    revealRubricCheck(checkId)
  }, [setParams])
  const { taskId: routeTaskId } = useParams()
  const refreshId = routeTaskId ?? task.task_id
  // Older review links still open the current work area.
  const requestedSection = params.get('section') ?? 'work'
  const section = requestedSection === 'review' ? 'work' : requestedSection
  const [contentDirty, setContentDirty] = useState(false)
  const [programsDirty, setProgramsDirty] = useState(false)
  const [executionPrograms, setExecutionPrograms] = useState(() => taskPrograms(task))
  const [rubricDirty, setRubricDirty] = useState(false)
  const [reviewDirty, setReviewDirty] = useState(false)
  const [error, setError] = useState('')
  const [decisionTarget, setDecisionTarget] = useState<HTMLDivElement | null>(null)
  const refreshing = useRef(false)
  const refresh = () => {
    if (refreshing.current) return
    refreshing.current = true
    api
      .get<TaskDetail>(`/tasks/${refreshId}`)
      .then(onSaved)
      .catch((e) => setError(e.message))
      .finally(() => {
        refreshing.current = false
      })
  }
  useEffect(() => {
    if (contentDirty || rubricDirty || programsDirty || reviewDirty) return
    const timer = window.setInterval(refresh, 5000)
    return () => window.clearInterval(timer)
  }, [refreshId, contentDirty, rubricDirty, programsDirty, reviewDirty])
  const retired = task.lifecycle === 'retired'
  const working = !retired && task.stage === 2 && role === 'sme'
  // A physics template is reviewed as a whole, with its tasks, on its own page.
  if (task.kind === 'scientific_workflow') return <WorkflowAuthoring data={data} role={role} onSaved={onSaved} onDirty={setReviewDirty} />
  if (task.collection && !retired) return <WorkflowReview task={task} onChanged={refresh} />
  return (
    <div className="task-page">
      <TaskHeading task={task} role={role} />
      <PublicationStatus task={task} role={role} onChanged={refresh} />
      {retired ? <RetirementNotice data={data} /> : <StageStepper stage={taskDisplayStage(task)} version={2} viewer={role} />}
      <QualityEditWarning changes={data.quality_changes} />
      <TaskTabs stage={task.stage} section={section} />
      <div className="review-task-layout">
        <RubricLinkProvider spec={shownImplementedRubric(task)} open={openRubricCheck}>
        <main>
          <div hidden={section !== 'work'} data-tour="task-work">
            {!retired && role === 'sme' && needsBuilderReply(task) &&
              <AuthoringReview data={data} role={role} />}
            {[2, 3].includes(task.stage) &&
              !(task.stage === 2 && task.returned_quality_round_id && task.last_feedback?.from_stage === 4) &&
              task.last_feedback &&
              task.last_feedback.to_stage === task.stage && (
                <section className="note">
                  <h3>Requested changes</h3>
                  <div className="who">
                    <Byline source={feedbackSource(task.last_feedback.from_role, role, task.last_feedback.from_id === task.sme_id)}
                      people={data.people} id={task.last_feedback.from_id} email={task.last_feedback.from_email}>
                      <span>{fmtDate(task.last_feedback.at)}</span>
                    </Byline>
                  </div>
                  <p className="prewrap">{task.last_feedback.message}</p>
                </section>
              )}
            {task.stage === 2 && task.returned_quality_round_id && <ReturnedQualityFeedback
              key={task.returned_quality_round_id} taskId={task.task_id}
              roundId={task.returned_quality_round_id} role={role} />}
            {!retired && task.stage === 0 && (
              <DraftReview
                task={task}
                role={role}
                refresh={refresh}
                dirty={contentDirty || rubricDirty}
                onDirty={setReviewDirty}
              />
            )}
            {task.stage === 2 && (
              <SubmissionPanel
                task={task}
                viewer={role}
                parent={data.parent}
                files={files}
                editable={working}
                onProgramsDirty={setProgramsDirty}
                onProgramsChange={setExecutionPrograms}
                onChanged={refresh}
                onSaved={onSaved}
              />
            )}
            {[1, 4].includes(task.stage) && task.review_round_id && (
              <ReviewPanel
                key={task.review_round_id}
                taskId={task.task_id}
                roundId={task.review_round_id}
                role={role}
                onChanged={refresh}
                onDirty={setReviewDirty}
                readOnly={retired}
                decisionBlocked={contentDirty || rubricDirty || programsDirty}
                inTask
                active={section === 'work'}
                decisionTarget={role === 'sme' ? decisionTarget : undefined}
              />
            )}
            {!retired && task.stage === 4 && role === 'engineer' && data.content_permissions?.can_edit && (
              <details className="box"><summary>Edit expert submission</summary>
                <SubmissionPanel task={task} files={files} parent={data.parent} viewer={role}
                  editable onChanged={refresh} onSaved={onSaved} onProgramsDirty={setProgramsDirty} />
              </details>
            )}
            {task.stage === 3 && task.build_waiting && <section className="build-waiting"><span className="build-waiting-icon"><Icon name="clock" size={22} /></span><div><h2>Waiting for environment support</h2><p>{task.build_waiting}</p><div className="chip-list">{taskPrograms(task).map(program => <span className="chip" key={program}>{program}</span>)}</div><p className="muted">Your submission is saved. No action is needed from you.</p></div></section>}
            {task.stage === 3 && !task.build_waiting &&
              (role === 'engineer' ? (
                <MonitorPanel task={task} onChanged={refresh} blocked={contentDirty || rubricDirty} />
              ) : (
                <section className="box">
                  <h2>Building the task package</h2>
                  <p className="muted">{retired ? 'Build work stopped when this task was retired.' : task.authoring?.cancel_reason === 'task_retired' ? 'The build is waiting for a developer to resume it.' : 'The agent is working from your reviewed submission.'}</p>
                </section>
              ))}
            {task.stage === 5 && (
              <section className="box">
                <h2>Published</h2>
                <p>The task and its review history are available here.</p>
              </section>
            )}
            <TaskLifecycle data={data} role={role} dirty={contentDirty || rubricDirty || programsDirty || reviewDirty} onSaved={onSaved} />
          </div>
          <div hidden={section !== 'brief'}>
            <TaskContent
              data={data}
              onSaved={onSaved}
              onDirty={setContentDirty}
              otherDirty={rubricDirty}
            />
          </div>
          <div hidden={section !== 'rubric'}>
            <RubricRefinement
              compareImplementation
              data={data}
              role={role}
              onSaved={onSaved}
              onDirty={setRubricDirty}
              otherDirty={contentDirty}
            />
            <details className="box">
              <summary>Optional passing and failing examples</summary>
              <ExamplesPanel
                taskId={task.task_id}
                rubric={task.rubric}
                files={files}
                editable={working}
                onChanged={refresh}
              />
            </details>
          </div>
          {section === 'coach' && (
            <CoachPanel taskId={task.task_id} role={role} showIntro={role === 'sme'} readOnly={retired}
              draft={working ? () => ({ programs: executionPrograms }) : undefined} />
          )}
          {section === 'files' && (
            <section className="box">
              <h2>Task files</h2>
              <FileList files={files} showKind />
            </section>
          )}
          {section === 'activity' && <TaskActivity task={task} events={data.events} role={role} authoringReviews={data.reviews} people={data.people} />}
        </main>
        </RubricLinkProvider>
        {!retired && role === 'sme' && [1, 4].includes(task.stage) && task.review_round_id && (
          <div className="review-decision-rail" ref={setDecisionTarget} />
        )}
        {!retired && [0, 2].includes(task.stage) && (
          <aside className="action-rail">
            <NextStep
              task={task}
              actions={data.actions}
              role={role}
              onDone={onSaved}
              blockedReason={contentDirty || rubricDirty || programsDirty ? 'Save your changes first.' : undefined}
            />
          </aside>
        )}
      </div>
      {error && (
        <div className="note err" role="alert">
          {error}
        </div>
      )}
    </div>
  )
}
